<template>
  <div class="text-puzzle-editor">
    <div v-if="selectedElement" class="card">
      <cardHeader :elementLabel="elementLabel" :elementType="selectedElement.el_Type" :mode="'Editor'" />
      <div class="card-body">
        <form @submit.prevent="saveElement">
          <!-- Hidden fields -->
          <div hidden class="mb-3">
            <label class="form-label">Element Label</label>
            <input type="text" class="form-control" :value="elementLabel" @input="handleLabelChange" />
          </div>
          <div hidden class="mb-3">
            <label class="form-label">Element ID</label>
            <input type="text" class="form-control" v-model="selectedElement.el_ID" readonly />
          </div>
          <div hidden class="mb-3" v-show="selectedElement.isNewElement">
            <label class="form-label">Type</label>
            <input type="text" class="form-control" v-model="selectedElement.el_Type" />
          </div>

          <!-- Main prompt -->
          <div class="mb-3">
            <h3>
              <input 
                placeholder="Main prompt" 
                type="text" 
                class="inputMainPrompt" 
                v-model="selectedElement.el_Text"
                @input="updateElement('el_Text', selectedElement.el_Text)"
              />
            </h3>
          </div>


          <!-- Rich text editor for main description -->
          <div class="form-group mb-3">
            <label class="form-label">Rich Text Instructions</label>
            <div ref="mainQuillEditor" class="quill-editor"></div>
          </div>


          <!-- Text Editor for Source -->
          <div class="mb-4">
            <label class="form-label fw-bold">Source Text - Mark segments to become draggable pieces. </label>
            <div class="border rounded p-2">
              <div id="quill-editor-source" ref="sourceQuillEditor"></div>
            </div>
            <div class="mt-2">
              <button 
                type="button"
                @click="markSelection" 
                class="btn btn-primary btn-sm me-2"
                :disabled="!hasSelection"
              >
                <i class="fas fa-puzzle-piece"></i> Mark as Draggable
              </button>
              <button 
                type="button"
                @click="clearAllGaps" 
                class="btn btn-outline-danger btn-sm"
                :disabled="!interactionData.gaps || interactionData.gaps.length === 0"
              >
                <i class="fas fa-trash"></i> Clear All Gaps
              </button>
            </div>
            <small class="text-muted">Select text and click "Mark as Draggable" to create gaps.</small>
          </div>

          <!-- Gap Management -->
          <div class="mb-4" v-if="interactionData.gaps && interactionData.gaps.length > 0">
            <label class="form-label fw-bold">Gaps Created ({{ interactionData.gaps.length }})</label>
            <div class="gap-list">
              <div
                v-for="(gap, index) in interactionData.gaps"
                :key="index"
                class="gap-item d-flex align-items-center mb-2 p-2 border rounded"
              >
                <span class="badge bg-secondary me-2">{{ index + 1 }}</span>
                <code class="flex-grow-1 me-2">{{ gap.text }}</code>
                <button
                  type="button"
                  @click="removeGap(index)"
                  class="btn btn-sm btn-outline-danger"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Distractor Management -->
          <div class="mb-4">
            <label class="form-label fw-bold">Distractors (Optional)</label>
            <div class="d-flex gap-2 mb-2">
              <input
                type="text"
                v-model="newDistractorText"
                class="form-control"
                placeholder="Enter distractor text"
                @keyup.enter="addDistractor"
              />
              <button
                type="button"
                @click="addDistractor"
                class="btn btn-primary"
                :disabled="!newDistractorText || !newDistractorText.trim()"
              >
                <i class="fas fa-plus"></i> Add Distractor
              </button>
            </div>
            <div class="distractor-list" v-if="interactionData.distractors && interactionData.distractors.length > 0">
              <div
                v-for="(distractor, index) in interactionData.distractors"
                :key="index"
                class="distractor-item d-flex align-items-center mb-2 p-2 border rounded"
              >
                <span class="badge bg-warning text-dark me-2">D{{ index + 1 }}</span>
                <code class="flex-grow-1 me-2">{{ distractor.text }}</code>
                <button
                  type="button"
                  @click="removeDistractor(index)"
                  class="btn btn-sm btn-outline-danger"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
            <small class="text-muted">Distractors are incorrect options that appear with correct answers.</small>
          </div>

          <!-- Scoring Configuration -->
          <div class="row mb-4">
            <div class="col-md-6">
              <label class="form-label fw-bold">Points per Correct Answer</label>
              <input 
                type="number" 
                v-model.number="interactionData.pointsPerCorrect" 
                class="form-control"
                min="0"
                step="0.1"
                @input="updateElement('el_InteractionData.pointsPerCorrect', interactionData.pointsPerCorrect)"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label fw-bold">Penalty per Incorrect Answer</label>
              <input 
                type="number" 
                v-model.number="interactionData.penaltyPerIncorrect" 
                class="form-control"
                min="0"
                step="0.1"
                @input="updateElement('el_InteractionData.penaltyPerIncorrect', interactionData.penaltyPerIncorrect)"
              />
            </div>
          </div>



          <!-- Feedback Configuration -->
          <div class="row mb-4">
            <div class="col-md-6">
              <label class="form-label fw-bold">Correct Feedback</label>
              <input 
                type="text" 
                v-model="selectedElement.feedback.correct" 
                class="form-control"
                placeholder="Correct!"
                @input="updateElement('feedback.correct', selectedElement.feedback.correct)"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label fw-bold">Incorrect Feedback</label>
              <input 
                type="text" 
                v-model="selectedElement.feedback.incorrect" 
                class="form-control"
                placeholder="Incorrect."
                @input="updateElement('feedback.incorrect', selectedElement.feedback.incorrect)"
              />
            </div>
          </div>
        </form>
      </div>
            
      <!-- Slider de difficulté -->
      <DifficultySlider 
        v-model="selectedElement.difficulty_rating"
        @update:model-value="updateElement('difficulty_rating', $event)"
      />
      
      <dimension v-if="hasDimensions" />
      <div class="card-footer">
        <EditorActionBar :selectedElement="selectedElement" :elementLabel="elementLabel" :isItemBank="isItemBank" />
      </div>
    </div>
    <div v-else class="alert alert-info">
      Select an element to edit
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import Quill from 'quill'
import cardHeader from '../default/cardHeader.vue'
import dimension from '../default/dimension.vue'
import EditorActionBar from '../default/EditorActionBar.vue'
import { useTestStore } from '../../../stores/testStore.js'

import DifficultySlider from '../../common/DifficultySlider.vue';
// Define props for Item Bank context adaptation
const props = defineProps({
  isItemBank: {
    type: Boolean,
    default: false
  }
})

const store = useTestStore()

// Computed pour vérifier si les dimensions doivent être affichées
const hasDimensions = computed(() => {
  const selectedEl = store.selectedElement;
  const testData = store.testData;
  
  // Afficher si l'élément a déjà des dimensions assignées
  const hasAssignedDimensions = selectedEl?.dimension && selectedEl.dimension.length > 0;
  
  // Afficher si des dimensions sont disponibles pour être ajoutées
  const hasAvailableDimensions = testData?.dimensions && testData.dimensions.length > 0;
  
  return hasAssignedDimensions || hasAvailableDimensions;
});

const mainQuillEditor = ref(null)
const sourceQuillEditor = ref(null)
const hasSelection = ref(false)
const newDistractorText = ref('')

let quillInstance = null
let sourceQuillInstance = null

const selectedElement = computed(() => {
  const element = store.selectedElement;
  if (!element) return null;

  // S'assurer que feedback existe avec les bonnes propriétés
  if (!element.feedback) {
    element.feedback = { correct: '', incorrect: '' };
  } else {
    if (element.feedback.correct === undefined) element.feedback.correct = '';
    if (element.feedback.incorrect === undefined) element.feedback.incorrect = '';
  }

  return element;
})

// Helper computed for interaction data with defaults
const interactionData = computed(() => {
  const element = selectedElement.value
  if (!element) return {
    el_SourceText: '',
    gaps: [],
    dragElements: [],
    distractors: [],
    pointsPerCorrect: 1,
    penaltyPerIncorrect: 0
  }

  const defaults = {
    el_SourceText: '',
    gaps: [],
    dragElements: [],
    distractors: [],
    pointsPerCorrect: 1,
    penaltyPerIncorrect: 0
  }

  return { ...defaults, ...(element.el_InteractionData || {}) }
})

// Synchronize label
const elementLabel = ref('')
watch(() => store.selectedElementLabel, (newLabel) => {
  elementLabel.value = newLabel || ''
}, { immediate: true })

const updateElement = (field, value) => {
  if (!selectedElement.value) return
  
  // Ensure el_InteractionData exists
  if (!selectedElement.value.el_InteractionData) {
    selectedElement.value.el_InteractionData = {}
  }
  
  const keys = field.split('.')
  let obj = selectedElement.value
  
  for (let i = 0; i < keys.length - 1; i++) {
    if (!obj[keys[i]]) {
      obj[keys[i]] = {}
    }
    obj = obj[keys[i]]
  }
  
  obj[keys[keys.length - 1]] = value
  
  // Always save gaps and dragElements data for persistence
  if (field.includes('el_InteractionData') || !props.isItemBank) {
    store.updateElement(selectedElement.value)
  }
}

const handleLabelChange = (event) => {
  elementLabel.value = event.target.value
  // Update the store if needed
}

const saveElement = () => {
  // Save element if needed
}

const initMainQuill = () => {
  if (mainQuillEditor.value && !quillInstance) {
    quillInstance = new Quill(mainQuillEditor.value, {
      theme: 'snow',
      modules: {
        toolbar: {
          container: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image'],
          ]
        }
      }
    })

    // Synchronisation manuelle au lieu de watcher
    syncMainQuillContent()

    quillInstance.on('text-change', () => {
      if (selectedElement.value) {
        selectedElement.value.el_RichText = quillInstance.root.innerHTML
        updateElement('el_RichText', selectedElement.value.el_RichText)
      }
    })
  }
}

const syncMainQuillContent = () => {
  if (quillInstance && selectedElement.value?.el_RichText) {
    quillInstance.root.innerHTML = selectedElement.value.el_RichText
  } else if (quillInstance) {
    quillInstance.root.innerHTML = ''
  }
}

const initSourceQuill = () => {
  if (sourceQuillEditor.value && !sourceQuillInstance) {
    sourceQuillInstance = new Quill(sourceQuillEditor.value, {
      theme: 'snow',
      modules: {
        toolbar: {
          container: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['clean']
          ]
        }
      }
    })

    // Synchronisation manuelle au lieu de watcher
    syncSourceQuillContent()

    sourceQuillInstance.on('selection-change', (range) => {
      hasSelection.value = range && range.length > 0
    })

    sourceQuillInstance.on('text-change', () => {
      if (selectedElement.value) {
        updateElement('el_InteractionData.el_SourceText', sourceQuillInstance.root.innerHTML)
      }
    })
  }
}

const syncSourceQuillContent = () => {
  if (sourceQuillInstance && interactionData.value?.el_SourceText) {
    sourceQuillInstance.root.innerHTML = interactionData.value.el_SourceText
  } else if (sourceQuillInstance) {
    sourceQuillInstance.root.innerHTML = ''
  }
}


const markSelection = () => {
  if (!sourceQuillInstance) return

  const range = sourceQuillInstance.getSelection()
  if (!range || range.length === 0) return

  const selectedText = sourceQuillInstance.getText(range.index, range.length).trim()
  if (!selectedText) return

  // Create a unique gap ID
  const gapId = `gap_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  // Ensure gaps array exists
  if (!interactionData.value.gaps) {
    updateElement('el_InteractionData.gaps', [])
  }

  // Create new gap with persistent ID
  const newGap = {
    id: gapId,
    text: selectedText,
    startIndex: range.index,
    length: range.length
  }

  // Add gap to the list
  const updatedGaps = [...(interactionData.value.gaps || []), newGap]
  updateElement('el_InteractionData.gaps', updatedGaps)

  // Replace selected text with gap placeholder
  sourceQuillInstance.deleteText(range.index, range.length)
  sourceQuillInstance.insertText(range.index, `[GAP_${updatedGaps.length}]`)

  // Update source text in the element and save
  const updatedContent = sourceQuillInstance.root.innerHTML
  updateElement('el_InteractionData.el_SourceText', updatedContent)

  // Update drag elements
  updateDragElements()
  
  // Force immediate save to ensure persistence
  store.saveTestDataToDatabase()
}

const removeGap = (index) => {
  const gap = interactionData.value.gaps[index]
  
  // Remove from gaps array
  const updatedGaps = [...interactionData.value.gaps]
  updatedGaps.splice(index, 1)
  updateElement('el_InteractionData.gaps', updatedGaps)

  // Update the text to remove the gap placeholder and restore original text
  const content = sourceQuillInstance.root.innerHTML
  const gapPlaceholder = `[GAP_${index + 1}]`
  const updatedContent = content.replace(gapPlaceholder, gap.text)
  
  sourceQuillInstance.root.innerHTML = updatedContent
  updateElement('el_InteractionData.el_SourceText', updatedContent)

  // Renumber remaining gaps
  renumberGaps()
  updateDragElements()
  
  // Force immediate save to ensure persistence
  store.saveTestDataToDatabase()
}

const renumberGaps = () => {
  let content = sourceQuillInstance.root.innerHTML
  interactionData.value.gaps.forEach((gap, index) => {
    const oldPlaceholder = new RegExp(`\\[GAP_\\d+\\]`)
    content = content.replace(oldPlaceholder, `[GAP_${index + 1}]`)
  })
  sourceQuillInstance.root.innerHTML = content
  updateElement('el_InteractionData.el_SourceText', content)
}

const clearAllGaps = () => {
  // Restore all gap texts
  let content = sourceQuillInstance.root.innerHTML
  interactionData.value.gaps.forEach((gap, index) => {
    content = content.replace(`[GAP_${index + 1}]`, gap.text)
  })
  
  sourceQuillInstance.root.innerHTML = content
  updateElement('el_InteractionData.el_SourceText', content)
  updateElement('el_InteractionData.gaps', [])
  updateElement('el_InteractionData.dragElements', [])
  
  // Force immediate save to ensure persistence
  store.saveTestDataToDatabase()
}

const updateDragElements = () => {
  // Combine gaps (correct) and distractors (incorrect)
  const gapElements = (interactionData.value.gaps || []).map(gap => ({
    id: gap.id,
    text: gap.text,
    isCorrect: true,
    gapId: gap.id
  }))

  const distractorElements = (interactionData.value.distractors || []).map(distractor => ({
    id: distractor.id,
    text: distractor.text,
    isCorrect: false,
    gapId: null // Distractors don't belong to any gap
  }))

  const allDragElements = [...gapElements, ...distractorElements]
  updateElement('el_InteractionData.dragElements', allDragElements)
}

const addDistractor = () => {
  const text = newDistractorText.value?.trim()
  if (!text) return

  // Create unique distractor ID
  const distractorId = `distractor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  // Ensure distractors array exists
  if (!interactionData.value.distractors) {
    updateElement('el_InteractionData.distractors', [])
  }

  // Create new distractor
  const newDistractor = {
    id: distractorId,
    text: text
  }

  // Add to distractors list
  const updatedDistractors = [...(interactionData.value.distractors || []), newDistractor]
  updateElement('el_InteractionData.distractors', updatedDistractors)

  // Update drag elements to include the new distractor
  updateDragElements()

  // Clear input
  newDistractorText.value = ''

  // Force immediate save to ensure persistence
  store.saveTestDataToDatabase()
}

const removeDistractor = (index) => {
  // Remove from distractors array
  const updatedDistractors = [...interactionData.value.distractors]
  updatedDistractors.splice(index, 1)
  updateElement('el_InteractionData.distractors', updatedDistractors)

  // Update drag elements
  updateDragElements()

  // Force immediate save to ensure persistence
  store.saveTestDataToDatabase()
}

// Synchronisation manuelle sans modification directe du DOM dans le watcher
watch(() => store.selectedElement, () => {
  // Délai pour éviter les conflits de timing
  nextTick(() => {
    syncMainQuillContent()
    syncSourceQuillContent()
  })
}, { deep: false }) // shallow watch pour éviter les problèmes

onMounted(() => {
  console.log('TextPuzzle onMounted...')
  
  // Diagnostics de direction
  console.log('Body direction:', document.body.dir, window.getComputedStyle(document.body).direction)
  console.log('HTML direction:', document.documentElement.dir)
  console.log('HTML lang:', document.documentElement.lang)
  
  nextTick(() => {
    initMainQuill()
    initSourceQuill()
  })
})
</script>

<style scoped>
.text-puzzle-editor {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.gap-list {
  max-height: 200px;
  overflow-y: auto;
}

.gap-item {
  background-color: #f8f9fa;
}

.gap-item code {
  background-color: #e9ecef;
  color: #495057;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.9em;
}

.distractor-list {
  max-height: 200px;
  overflow-y: auto;
}

.distractor-item {
  background-color: #fff9e6;
}

.distractor-item code {
  background-color: #ffe9a3;
  color: #856404;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.9em;
}


.form-label.fw-bold {
  color: #495057;
  font-size: 0.95em;
}

.quill-editor {
  min-height: 120px;
}

#quill-editor-source {
  min-height: 150px;
}


.inputMainPrompt {
  width: 100%;
  border: none;
  background: transparent;
  font-size: 1.5rem;
  font-weight: 500;
  color: #495057;
}

.inputMainPrompt:focus {
  outline: none;
  border-bottom: 2px solid #007bff;
}

.card-header{
  color:white;
  background-color: #426C9C;
}
</style>